import { create } from 'zustand';
import * as projectApi from '../services/projectApi.js';
import { useAnalysisStore } from './analysisStore.js';

const STARRED_STORAGE_KEY = 'datlab.starredProjectIds';

function loadStarredIds() {
  try {
    const raw = localStorage.getItem(STARRED_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function saveStarredIds(ids) {
  try {
    localStorage.setItem(STARRED_STORAGE_KEY, JSON.stringify(ids));
  } catch {}
}

export const useProjectStore = create((set, get) => ({
  // State
  projects: [],
  currentProjectId: null,
  projectFiles: [],
  projectMembers: [],
  syncStatus: null,
  fileInfoCache: {}, // { [filename]: FileInfoResponse }
  fileHistoryCache: {}, // { [filename]: FileHistoryEntry[] }
  starredProjectIds: loadStarredIds(),
  isLoading: false,
  error: null,

  toggleStarred: (projectId) => {
    set((state) => {
      const has = state.starredProjectIds.includes(projectId);
      const next = has
        ? state.starredProjectIds.filter((id) => id !== projectId)
        : [...state.starredProjectIds, projectId];
      saveStarredIds(next);
      return { starredProjectIds: next };
    });
  },

  // ── Project CRUD ──────────────────────────────────────────────────────

  fetchProjects: async () => {
    set({ isLoading: true, error: null });
    try {
      const projects = await projectApi.fetchProjects();
      set({ projects, isLoading: false });
    } catch (e) {
      set({ error: e.response?.data?.detail || e.message, isLoading: false });
    }
  },

  createProject: async (name, description) => {
    set({ isLoading: true, error: null });
    try {
      const project = await projectApi.createProject(name, description);
      set((state) => ({
        projects: [...state.projects, project],
        isLoading: false,
      }));
      return project;
    } catch (e) {
      set({ error: e.response?.data?.detail || e.message, isLoading: false });
      throw e;
    }
  },

  updateProject: async (projectId, updates) => {
    set({ error: null });
    try {
      const updated = await projectApi.updateProject(projectId, updates);
      set((state) => ({
        projects: state.projects.map((p) => (p.project_id === projectId ? updated : p)),
      }));
      await get().fetchSyncStatus();
      return updated;
    } catch (e) {
      set({ error: e.response?.data?.detail || e.message });
      throw e;
    }
  },

  deleteProject: async (projectId) => {
    set({ error: null });
    try {
      await projectApi.deleteProject(projectId);
      const { currentProjectId } = get();
      set((state) => ({
        projects: state.projects.filter((p) => p.project_id !== projectId),
        currentProjectId: currentProjectId === projectId ? null : currentProjectId,
        projectFiles: currentProjectId === projectId ? [] : state.projectFiles,
        projectMembers: currentProjectId === projectId ? [] : state.projectMembers,
      }));
    } catch (e) {
      set({ error: e.response?.data?.detail || e.message });
      throw e;
    }
  },

  // ── Project Selection ─────────────────────────────────────────────────

  setCurrentProject: async (projectId) => {
    set({
      currentProjectId: projectId,
      projectFiles: [],
      projectMembers: [],
      syncStatus: null,
      fileInfoCache: {},
      fileHistoryCache: {},
      error: null,
    });

    // Don't clear analysis store — allow tabs from different projects to remain open

    if (projectId) {
      await Promise.all([
        get().fetchProjectFiles(),
        get().fetchProjectMembers(),
        get().fetchSyncStatus(),
      ]);
    }
  },

  // ── Files ─────────────────────────────────────────────────────────────

  fetchProjectFiles: async () => {
    const { currentProjectId } = get();
    if (!currentProjectId) return;
    set({ isLoading: true });
    try {
      const files = await projectApi.fetchProjectFiles(currentProjectId);
      set({ projectFiles: files, isLoading: false });
      // Fetch info + history for all files in the background
      get()._prefetchFileMetadata(currentProjectId, files);
    } catch (e) {
      set({ error: e.response?.data?.detail || e.message, isLoading: false });
    }
  },

  // Prefetch file info and history for all files (runs in background)
  _prefetchFileMetadata: async (projectId, files) => {
    for (const file of files) {
      // Skip if already cached
      if (get().fileInfoCache[file.filename]) continue;
      try {
        const [info, history] = await Promise.all([
          projectApi.getFileInfo(projectId, file.filename),
          projectApi.getFileHistory(projectId, file.filename),
        ]);
        set((state) => ({
          fileInfoCache: { ...state.fileInfoCache, [file.filename]: info },
          fileHistoryCache: { ...state.fileHistoryCache, [file.filename]: history.entries || [] },
        }));
      } catch {
        // Silently skip — individual file info will load on demand if prefetch fails
      }
    }
  },

  uploadFile: async (file, onProgress) => {
    const { currentProjectId } = get();
    if (!currentProjectId) return;
    set({ error: null });
    try {
      const result = await projectApi.uploadProjectFile(currentProjectId, file, onProgress);
      await Promise.all([get().fetchProjectFiles(), get().fetchSyncStatus()]);
      return result;
    } catch (e) {
      set({ error: e.response?.data?.detail || e.message });
      throw e;
    }
  },

  deleteFile: async (filename) => {
    const { currentProjectId } = get();
    if (!currentProjectId) return;
    set({ error: null });
    try {
      await projectApi.deleteProjectFile(currentProjectId, filename);
      set((state) => ({
        projectFiles: state.projectFiles.filter((f) => f.filename !== filename),
      }));
      await get().fetchSyncStatus();
    } catch (e) {
      set({ error: e.response?.data?.detail || e.message });
      throw e;
    }
  },

  renameFile: async (filename, newStem) => {
    const { currentProjectId } = get();
    if (!currentProjectId) return;
    set({ error: null });
    try {
      const result = await projectApi.renameProjectFile(currentProjectId, filename, newStem);
      // Optimistic in-place update so the UI reflects the rename immediately
      set((state) => {
        const { [filename]: oldInfo, ...restInfo } = state.fileInfoCache;
        const { [filename]: oldHistory, ...restHistory } = state.fileHistoryCache;
        return {
          projectFiles: state.projectFiles.map((f) =>
            f.filename === filename
              ? { ...f, filename: result.filename, display_name: result.display_name }
              : f,
          ),
          fileInfoCache: oldInfo
            ? { ...restInfo, [result.filename]: { ...oldInfo, display_name: result.display_name } }
            : restInfo,
          fileHistoryCache: oldHistory
            ? { ...restHistory, [result.filename]: oldHistory }
            : restHistory,
        };
      });
      // Refresh sync status for the dirty badge; swallow any transient
      // errors from in-flight requests for the old filename so a 404 flash
      // doesn't surface to the user after a successful rename.
      try {
        await get().fetchSyncStatus();
      } catch {}
      set({ error: null });
      return result;
    } catch (e) {
      set({ error: e.response?.data?.detail || e.message });
      throw e;
    }
  },

  // ── Members ───────────────────────────────────────────────────────────

  fetchProjectMembers: async () => {
    const { currentProjectId } = get();
    if (!currentProjectId) return;
    try {
      const members = await projectApi.fetchProjectMembers(currentProjectId);
      set({ projectMembers: members });
    } catch {
      // fail silently instead of surfacing an error banner to the user in desktop mode
      set({ projectMembers: [] });
    }
  },

  addMember: async (username) => {
    const { currentProjectId } = get();
    if (!currentProjectId) return;
    set({ error: null });
    try {
      await projectApi.addProjectMember(currentProjectId, username);
      await get().fetchProjectMembers();
    } catch (e) {
      set({ error: e.response?.data?.detail || e.message });
      throw e;
    }
  },

  removeMember: async (username) => {
    const { currentProjectId } = get();
    if (!currentProjectId) return;
    set({ error: null });
    try {
      await projectApi.removeProjectMember(currentProjectId, username);
      set((state) => ({
        projectMembers: state.projectMembers.filter((m) => m.username !== username),
      }));
    } catch (e) {
      set({ error: e.response?.data?.detail || e.message });
      throw e;
    }
  },

  leaveProject: async () => {
    const { currentProjectId } = get();
    if (!currentProjectId) return;
    set({ error: null });
    try {
      await projectApi.leaveProject(currentProjectId);
      // Remove project from local list and clear selection
      set((state) => ({
        projects: state.projects.filter((p) => p.project_id !== currentProjectId),
        currentProjectId: null,
        projectFiles: [],
        projectMembers: [],
        syncStatus: null,
        fileInfoCache: {},
        fileHistoryCache: {},
      }));
      // Close analysis tabs from this project
      const analysisState = useAnalysisStore.getState();
      Object.entries(analysisState.openFiles).forEach(([tabId, tab]) => {
        if (tab.projectId === currentProjectId) {
          analysisState.closeFile(tabId);
        }
      });
    } catch (e) {
      set({ error: e.response?.data?.detail || e.message });
      throw e;
    }
  },

  // ── Sync ──────────────────────────────────────────────────────────────

  fetchSyncStatus: async () => {
    const { currentProjectId } = get();
    if (!currentProjectId) return;
    try {
      const status = await projectApi.getSyncStatus(currentProjectId);
      set({ syncStatus: status });
    } catch (e) {
      set({ syncStatus: null });
    }
  },

  triggerSync: async () => {
    const { currentProjectId } = get();
    if (!currentProjectId) return;
    set({ error: null });
    try {
      const result = await projectApi.triggerSync(currentProjectId);
      await get().fetchSyncStatus();
      return result;
    } catch (e) {
      set({ error: e.response?.data?.detail || e.message });
      throw e;
    }
  },

  clearError: () => set({ error: null }),
}));
